import { Fade, IconButton, styled, useTheme } from '@mui/material';
import { FC, useCallback } from 'react';
import { Typography } from '../../../../../components/Typography/Typography';
import { ReactComponent as AddIcon } from '../../../../../assets/icons/add-gray.svg';
import { QUESTION_TYPE, SurveyTemplateSection } from '../../../../../types';
import {
  activeSectionIdState,
  isApplyingEvaluationRulesModeActiveState,
  isMultiActionMoveModeActiveState,
  isSingleActionMoveModeActiveState,
  sectionsState,
  selectedQuestionsState,
  surveyTemplatesDataState,
} from '../../../../../state/UIState';
import { useAtom } from 'jotai';
import { TemplateQuestionList } from './TemplateQuestionList';
import { generateId } from '../../../../../utils/generateId';
import { Tooltip } from '../../../../../components/Tooltip/Tooltip';
import { Accordion } from '../../../../../components/Accordion/Accordion';

interface Props {
  sections: SurveyTemplateSection[];
}

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const TemplateSectionList: FC<Props> = ({ sections: templateSections }) => {
  const { colors } = useTheme();
  const [, setSurveyTemplateData] = useAtom(surveyTemplatesDataState);
  const [, setSections] = useAtom(sectionsState);
  const [, setActiveSectionId] = useAtom(activeSectionIdState);
  const [isApplyingModeActive] = useAtom(isApplyingEvaluationRulesModeActiveState);
  const [isSingleActionMoveModeActive] = useAtom(isSingleActionMoveModeActiveState);
  const [isMultiActionMoveModeActive] = useAtom(isMultiActionMoveModeActiveState);
  const [selectedQuestions] = useAtom(selectedQuestionsState);

  const onExpandOrCollapse = useCallback(
    (section: SurveyTemplateSection) => {
      setSurveyTemplateData((prevCategories) => {
        return prevCategories.map((category) => {
          return category.name === section.category?.name ||
            (category.name === 'Uncategorized' && !section.category)
            ? {
                ...category,
                sections: category.sections.map((s) => {
                  return s.id === section.id
                    ? {
                        ...s,
                        isExpanded: !s.isExpanded,
                      }
                    : s;
                }),
              }
            : category;
        });
      });
    },
    [setSurveyTemplateData]
  );

  const onAddSectionToSurvey = useCallback(
    (section: SurveyTemplateSection) => {
      const newSectionId = generateId();
      setSections((prevSections) => {
        return [
          ...prevSections,
          {
            id: newSectionId,
            name: section.name,
            depth: 0,
            parentId: null,
            questions: section.questions.map((question) => ({
              id: generateId(),
              includeInstructions: question.includeInstructions,
              includeEvaluation: question.includeEvaluation,
              evaluationRules: question.evaluationRules || null,
              instructions: question.instructions,
              isActive: true,
              name: question.name,
              type: question.type,
              tableData: question.tableData,
              options:
                question.options?.map((option) => ({
                  id: generateId(),
                  name: option.name,
                })) || [],
              sectionId: newSectionId,
              isRequired: question.type === QUESTION_TYPE.TABLE ? false : true,
            })),
            subSections: [],
            isExpanded: false,
          },
        ];
      });
      setActiveSectionId(newSectionId);
    },
    [setActiveSectionId, setSections]
  );

  return (
    <Wrapper>
      {templateSections.map((section) => (
        <Accordion
          key={section.id}
          title={section.name}
          isExpanded={section.isExpanded}
          onExpandToggle={() => onExpandOrCollapse(section)}
          suffixButton={
            <Tooltip
              title='Add Section to Survey'
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              PopperProps={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [70, -13],
                    },
                  },
                ],
              }}
            >
              <IconButton
                onClick={() => onAddSectionToSurvey(section)}
                style={{
                  visibility:
                    isApplyingModeActive ||
                    isSingleActionMoveModeActive ||
                    isMultiActionMoveModeActive ||
                    selectedQuestions.length
                      ? 'hidden'
                      : 'visible',
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          }
          collapsedContent={
            <Typography variant='body' color={colors.primary[70]} style={{ paddingLeft: '32px' }}>
              {section.questions.length === 1
                ? '1 question'
                : `${section.questions.length} questions`}
            </Typography>
          }
        >
          <TemplateQuestionList questions={section.questions} />
        </Accordion>
      ))}
    </Wrapper>
  );
};
